/**
 * Tiny History-API router. Path-based, base-path aware (the app is served under
 * `import.meta.env.BASE_URL` — `/app/` behind kip-backend, `/` in dev/preview).
 * Exposes a Svelte 5 rune-based store.
 */

export interface Route {
  path: string;
  params: Record<string, string>;
}

/** Trailing slash kept, e.g. "/app/" -> "/app". "/" -> "". */
export const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

/** App-relative path from a full pathname (strips BASE). */
function toAppPath(pathname: string): string {
  let p = pathname;
  if (BASE && p.startsWith(BASE)) p = p.slice(BASE.length);
  return p.replace(/\/+$/, '') || '/';
}

/** Full pathname from an app-relative path (adds BASE). */
export function href(appPath: string): string {
  return `${BASE}${appPath}` || '/';
}

function parse(pathname: string): Route {
  const clean = toAppPath(pathname);
  const pageMatch = /^\/page\/(.+)$/.exec(clean);
  if (pageMatch) return { path: '/page/:slug', params: { slug: decodeURIComponent(pageMatch[1]!) } };
  return { path: clean, params: {} };
}

class Router {
  current = $state<Route>(parse(location.pathname));

  constructor() {
    addEventListener('popstate', () => {
      this.current = parse(location.pathname);
    });
  }

  /** `to` is app-relative (e.g. "/read"). */
  go(to: string, opts: { replace?: boolean } = {}): void {
    const full = href(to);
    if (full === location.pathname) return;
    history[opts.replace ? 'replaceState' : 'pushState']({}, '', full);
    this.current = parse(full);
    scrollTo(0, 0);
  }

  /** Navigate from a full pathname (e.g. an anchor's href). */
  goHref(fullPath: string): void {
    this.go(toAppPath(fullPath));
  }
}

export const router = new Router();

/** `<a use:link href={href('/read')}>` — intercept same-origin nav via History. */
export function link(node: HTMLAnchorElement) {
  const onClick = (e: MouseEvent) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
    const target = node.getAttribute('href');
    if (!target || !target.startsWith('/')) return;
    e.preventDefault();
    router.goHref(node.pathname);
  };
  node.addEventListener('click', onClick);
  return { destroy: () => node.removeEventListener('click', onClick) };
}
